import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ALL_STATUSES } from "../components/ops/opsUtils";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import EntitySearchSelect from "../components/admin/EntitySearchSelect";
import { toast } from "sonner";

export default function AddFunding() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    attorney_email: "",
    attorney_name: "",
    attorney_firm: "",
    firm_id: "",
    firm_label: "",
    case_type: "Auto",
    case_state: "OH",
    principal: 1500,
    monthly_rate: 0.038,
    funding_date: new Date().toISOString().slice(0, 10),
    max_tiers: 6,
    current_status: "Funded",
    note: "",
  });
  const u = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        principal: Number(f.principal),
        monthly_rate: Number(f.monthly_rate),
        max_tiers: parseInt(f.max_tiers, 10),
        funding_date: new Date(f.funding_date).toISOString(),
      };
      delete payload.note;
      delete payload.firm_label;
      if (!payload.firm_id) delete payload.firm_id;
      const { data } = await api.post("/operations/loans", payload);
      if (f.note.trim()) {
        await api.post(`/operations/loans/${data.loan_id}/notes`, { content: f.note.trim() });
      }
      toast.success(`Loan ${data.loan_id} added`);
      navigate(`/admin/loans/${data.loan_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  // Live payoff preview
  const months = Math.max(0, Math.round((Date.now() - new Date(f.funding_date).getTime()) / (1000 * 60 * 60 * 24 * 30.4375)));
  const tiers = Math.max(1, Math.ceil(months / 6));
  const previewPayoff = Number(f.principal || 0) * Math.pow(1 + Number(f.monthly_rate || 0), 6 * tiers);

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="add-funding-page">
      <Link to="/admin/loans" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-loans">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All loans
      </Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Add funding</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-6">Create a new funded loan. Updates dashboard metrics instantly.</p>

      <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-6">
        <Section title="Client">
          <Row><Field label="Full name *"><Input required value={f.client_name} onChange={u("client_name")} placeholder="Jane Doe" data-testid="af-client-name" /></Field>
               <Field label="Email"><Input type="email" value={f.client_email} onChange={u("client_email")} placeholder="jane@example.com" data-testid="af-client-email" /></Field></Row>
          <Row><Field label="Phone"><Input value={f.client_phone} onChange={u("client_phone")} placeholder="(555) 123-4567" data-testid="af-client-phone" /></Field>
               <Field label="State"><Input value={f.case_state} onChange={u("case_state")} placeholder="OH" maxLength={2} data-testid="af-state" /></Field></Row>
        </Section>

        <Section title="Attorney & Firm">
          <Row>
            <Field label="Attorney *">
              <EntitySearchSelect
                kind="attorney"
                value={f.attorney_email}
                onSelect={(row) => setF((s) => ({
                  ...s,
                  attorney_email: row.email || "",
                  attorney_name: row.name || s.attorney_name,
                  attorney_firm: row.firm || s.attorney_firm,
                  firm_id: row.firm_id || s.firm_id,
                  firm_label: row.firm_id ? (row.firm || s.firm_label) : s.firm_label,
                }))}
                onCreateNew={(t) => setF((s) => ({ ...s, attorney_email: t }))}
                placeholder="Type email or name…"
                testidPrefix="af-attorney-search"
              />
              <input type="hidden" required value={f.attorney_email} readOnly data-testid="af-attorney-email" />
              <div className="text-[11px] text-[#6B7280] mt-1">Picking an existing attorney auto-fills their firm.</div>
            </Field>
            <Field label="Attorney name"><Input value={f.attorney_name} onChange={u("attorney_name")} placeholder="Sandra Whitfield" data-testid="af-attorney-name" /></Field>
          </Row>
          <Row>
            <Field label="Law firm (portal link)">
              <EntitySearchSelect
                kind="firm"
                value={f.firm_label || f.attorney_firm}
                onSelect={(row) => setF((s) => ({ ...s, firm_id: row.firm_id, firm_label: row.name, attorney_firm: row.name || s.attorney_firm }))}
                onCreateNew={(t) => setF((s) => ({ ...s, firm_id: "", firm_label: t, attorney_firm: t }))}
                placeholder="Search firms (links the loan to the firm portal)…"
                testidPrefix="af-firm-search"
              />
              <div className="text-[11px] text-[#6B7280] mt-1">{f.firm_id ? <span className="text-[#047857]">Linked to firm portal ✓</span> : "Optional — will be inferred from attorney later if blank."}</div>
            </Field>
            <Field label="Case type">
              <Select value={f.case_type} onValueChange={u("case_type")}>
                <SelectTrigger data-testid="af-case-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Auto", "Slip & Fall", "Premises Liability", "Workplace Injury", "Wrongful Death", "Medical Malpractice", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </Row>
        </Section>

        <Section title="Loan terms">
          <Row><Field label="Principal *">
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span><Input required type="number" min="100" step="100" value={f.principal} onChange={u("principal")} className="pl-7" data-testid="af-principal" /></div>
               </Field>
               <Field label="Monthly rate *">
                 <Input required type="number" min="0.01" max="0.10" step="0.001" value={f.monthly_rate} onChange={u("monthly_rate")} data-testid="af-rate" />
                 <div className="text-[11px] text-[#6B7280] mt-1">{(Number(f.monthly_rate) * 100).toFixed(2)}% per month, 6-month tier compounding</div>
               </Field></Row>
          <Row><Field label="Funding date *"><Input required type="date" value={f.funding_date} onChange={u("funding_date")} data-testid="af-funding-date" /></Field>
               <Field label="Max tiers"><Input type="number" min="1" max="12" value={f.max_tiers} onChange={u("max_tiers")} data-testid="af-max-tiers" /></Field></Row>
          <Row><Field label="Initial status">
                <Select value={f.current_status} onValueChange={u("current_status")}>
                  <SelectTrigger data-testid="af-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
               </Field>
               <Field label=""><div /></Field></Row>
        </Section>

        <Section title="Notes (optional)">
          <Textarea rows={2} placeholder="Anything the team should know about this advance…" value={f.note} onChange={u("note")} data-testid="af-note" />
        </Section>

        {/* Live preview */}
        <div className="rounded-lg bg-[#F0F6FF] border border-[#DBEAFE] p-4 flex items-center justify-between" data-testid="af-preview">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-[#1D4ED8] uppercase tracking-wider font-medium"><Sparkles className="w-3 h-3" /> Live preview</div>
            <div className="text-xs text-[#0A2540] mt-1">{tiers} tier{tiers !== 1 ? "s" : ""} elapsed · payoff at next settlement window</div>
          </div>
          <div className="font-display text-2xl text-[#0A2540] tabular-nums">${previewPayoff.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F3F4F6]">
          <Link to="/admin/loans"><Button type="button" variant="ghost" data-testid="af-cancel">Cancel</Button></Link>
          <Button type="submit" disabled={submitting} className="bg-[#0066FF] hover:bg-[#0052CC] h-10 px-6" data-testid="af-submit">
            {submitting ? "Adding…" : "Add funding"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-xs uppercase tracking-wider text-[#6B7280] font-medium mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
